/**
 * Meta Pixel id from env. Never hardcode a pixel id in the repo.
 * Runtime Fly secret META_PIXEL_ID is injected as window.__META_PIXEL_ID__
 * during SSR (see server/renderer.js) so the client bundle does not need a
 * baked-in id. When the secret is unset, callers must load no Meta script.
 *
 * CommonJS so server/renderer.js can require the same check the client uses.
 */

const PIXEL_ID = /^\d{15,20}$/;

const normalizeMetaPixelId = value => {
  const id = typeof value === 'string' ? value.trim() : '';
  return PIXEL_ID.test(id) ? id : null;
};

/**
 * Resolve the Pixel id from env or the SSR window hook.
 *
 * @returns {string|null}
 */
const envMetaPixelId = () => {
  const fromEnv = normalizeMetaPixelId(
    process.env.META_PIXEL_ID || process.env.REACT_APP_META_PIXEL_ID
  );
  if (fromEnv) {
    return fromEnv;
  }
  if (typeof window !== 'undefined') {
    return normalizeMetaPixelId(window.__META_PIXEL_ID__);
  }
  return null;
};

/**
 * Inline script that exposes the runtime id to the hydrated client.
 *
 * @param {string|null|undefined} id
 * @returns {string}
 */
const metaPixelIdInlineScript = id => {
  const safe = normalizeMetaPixelId(id);
  if (!safe) {
    return '';
  }
  return `window.__META_PIXEL_ID__=${JSON.stringify(safe)};`;
};

/**
 * Nonce-bearing script tag for the SSR document. Empty when no valid id is set.
 *
 * @param {string} [nonce]
 * @returns {string}
 */
const metaPixelIdBootTag = nonce => {
  const body = metaPixelIdInlineScript(
    process.env.META_PIXEL_ID || process.env.REACT_APP_META_PIXEL_ID || ''
  );
  if (!body) {
    return '';
  }
  const nonceMaybe = nonce ? `nonce="${nonce}"` : '';
  return `<script ${nonceMaybe}>${body}</script>`;
};

/**
 * Official fbevents.js queue stub, init, and the first PageView.
 * Safe to call on every render: a second call for the same id does nothing.
 * History page views are sent manually (SPA), matching GA page_view.
 *
 * @param {string|null|undefined} pixelId
 * @returns {boolean} true when this call performed init
 */
const bootMetaPixel = pixelId => {
  const id = normalizeMetaPixelId(pixelId);
  if (!id || typeof window === 'undefined') {
    return false;
  }
  if (window.__META_PIXEL_BOOTED__ === id) {
    return false;
  }

  if (typeof window.fbq !== 'function') {
    const fbq = function fbq() {
      if (typeof fbq.callMethod === 'function') {
        fbq.callMethod.apply(fbq, arguments);
      } else {
        fbq.queue.push(arguments);
      }
    };
    if (!window._fbq) {
      window._fbq = fbq;
    }
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
  }

  // fbevents.js wraps history.pushState and emits its own PageView.
  // This app already sends one PageView per navigation, the same way GA does.
  window.fbq.disablePushState = true;
  window.fbq('init', id);
  window.fbq('track', 'PageView');
  window.__META_PIXEL_BOOTED__ = id;
  return true;
};

const META_PIXEL_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

module.exports = {
  META_PIXEL_SCRIPT_SRC,
  normalizeMetaPixelId,
  envMetaPixelId,
  metaPixelIdInlineScript,
  metaPixelIdBootTag,
  bootMetaPixel,
};
